import { Container } from 'pixi.js';
import type { PublicPlayerAppearance } from '@push.io/shared';
import type { GraphicsProfile } from './graphics-quality.js';
import { BaseCircleLayer } from './layers/BaseCircleLayer.js';
import { PatternLayer } from './layers/PatternLayer.js';
import { FlagLayer } from './layers/FlagLayer.js';
import { AccessoryLayer } from './layers/AccessoryLayer.js';
import { TrailLayer } from './layers/TrailLayer.js';
import { NameLayer } from './layers/NameLayer.js';
import { ShadowLayer } from './layers/ShadowLayer.js';
import { OutlineLayer } from './layers/OutlineLayer.js';
import { EmptyLayer } from './layers/EmptyLayer.js';

export class PlayerView {
  readonly root: Container;
  readonly playerId: string;
  private shadow: ShadowLayer;
  private trail: TrailLayer;
  private base: BaseCircleLayer;
  private pattern: PatternLayer;
  private flag: FlagLayer;
  private accessory: AccessoryLayer;
  private outline: OutlineLayer;
  private nameLayer: NameLayer;
  private letter: EmptyLayer;
  private nameplate: EmptyLayer;
  private badge: EmptyLayer;
  private effects: EmptyLayer;

  private currentAppearance: PublicPlayerAppearance | null = null;
  private currentRadius: number = 0;
  private currentIsLocal: boolean = false;
  private currentName: string = '';
  private quality: GraphicsProfile | null = null;
  private _destroyed = false;

  constructor(playerId: string) {
    this.playerId = playerId;
    this.root = new Container();

    this.shadow = new ShadowLayer();
    this.trail = new TrailLayer();
    this.base = new BaseCircleLayer();
    this.pattern = new PatternLayer();
    this.flag = new FlagLayer();
    this.letter = new EmptyLayer();
    this.accessory = new AccessoryLayer();
    this.outline = new OutlineLayer();
    this.nameLayer = new NameLayer();
    this.nameplate = new EmptyLayer();
    this.badge = new EmptyLayer();
    this.effects = new EmptyLayer();

    // All visual state is local to this root. Physics updates only root.position.
    this.root.addChild(this.shadow.container);
    this.root.addChild(this.trail.container);
    this.root.addChild(this.base.container);
    this.root.addChild(this.pattern.container);
    this.root.addChild(this.flag.container);
    this.root.addChild(this.letter.container);
    this.root.addChild(this.outline.container);
    this.root.addChild(this.accessory.container);
    this.root.addChild(this.nameLayer.container);
    this.root.addChild(this.nameplate.container);
    this.root.addChild(this.badge.container);
    this.root.addChild(this.effects.container);
  }

  updateLayers(appearance: PublicPlayerAppearance | null, radius: number, isLocal: boolean, name: string): void {
    if (this._destroyed) return;
    const appearanceChanged = !sameAppearance(appearance, this.currentAppearance);
    const radiusChanged = radius !== this.currentRadius;
    const localChanged = isLocal !== this.currentIsLocal;
    const nameChanged = name !== this.currentName;

    if (appearanceChanged || radiusChanged) {
      this.currentAppearance = appearance;
      this.currentRadius = radius;
      this.base.update(appearance, radius, isLocal);
      this.pattern.update(appearance, radius);
      this.flag.update(appearance, radius);
    }

    if (appearanceChanged || radiusChanged || localChanged) {
      this.currentIsLocal = isLocal;
      this.accessory.update(appearance, radius, isLocal);
      this.outline.update(radius, isLocal);
    }

    if (appearanceChanged || radiusChanged) {
      this.shadow.update(radius);
    }

    if (nameChanged || radiusChanged) {
      this.currentName = name;
      this.nameLayer.update(name, radius);
    }
    if (this.quality) this.applyQuality(this.quality);
  }

  updateTrail(deltaMs: number, screenX: number, screenY: number, vx: number, vy: number, appearance: PublicPlayerAppearance | null, profile: GraphicsProfile): void {
    if (this._destroyed) return;
    this.trail.setSpeed(vx, vy);
    this.trail.update(deltaMs, screenX, screenY, appearance, profile);
  }

  setQuality(profile: GraphicsProfile): void {
    if (this._destroyed) return;
    this.quality = profile;
    this.applyQuality(profile);
  }

  private applyQuality(profile: GraphicsProfile): void {
    this.shadow.setVisible(profile.shadow);
    this.pattern.setVisible(profile.patterns);
    this.flag.setVisible(profile.flags);
    this.accessory.setVisible(profile.accessories);
    this.nameLayer.setVisible(profile.nameplates);
    this.nameplate.setVisible(profile.nameplates);
    this.badge.setVisible(profile.badges);
    this.effects.setVisible(profile.effects);
    this.trail.setVisible(profile.trailLength > 0);
  }

  destroy(): void {
    if (this._destroyed) return;
    this._destroyed = true;
    this.shadow.destroy();
    this.trail.destroy();
    this.base.destroy();
    this.pattern.destroy();
    this.flag.destroy();
    this.accessory.destroy();
    this.outline.destroy();
    this.nameLayer.destroy();
    this.letter.destroy();
    this.nameplate.destroy();
    this.badge.destroy();
    this.effects.destroy();
    this.root.removeChildren();
    this.root.destroy();
  }

  get destroyed(): boolean {
    return this._destroyed;
  }

  get particleCount(): number {
    return this.trail.activeCount;
  }
}

const APPEARANCE_KEYS: ReadonlyArray<keyof PublicPlayerAppearance> = [
  'baseColorId', 'skinId', 'patternId', 'flagId', 'letterId', 'accessoryId',
  'accessoryColor', 'trailId', 'nameplateId', 'badgeId', 'victoryEffectId',
];

function sameAppearance(a: PublicPlayerAppearance | null, b: PublicPlayerAppearance | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return APPEARANCE_KEYS.every((key) => a[key] === b[key]);
}
